import React from "react";
import Modal from "react-bootstrap/Modal";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Col,
  FloatingLabel,
  Form,
  Row,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { InfoCircle } from "react-bootstrap-icons";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { UserRole } from "components/Reports/reportAssets/assets";
import {
  ControllerRoles,
  ValidationControllerData,
  controllerData,
  specialties,
} from "./controllerTypes";
import { isValidIsraeliID } from "../../UsersPermissionsManagementUtils";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: null) => void;
  submitForm: (values: controllerData) => void;
  initialValues: controllerData;
  isForUpdate: boolean;
};

const AddOrUpdateModal = ({
  isOpen,
  setIsOpen,
  submitForm,
  initialValues,
  isForUpdate,
}: Props) => {
  const { t } = useTranslation();
  const validationSchema: Yup.ObjectSchema<ValidationControllerData> =
    Yup.object().shape({
      fullName: Yup.string()
        .max(20, t("חריגה מכמות התווים"))
        .required(t("required")),
      phoneNumber: Yup.string()
        .max(10, t("חריגה מכמות התווים"))
        .matches(/^0\d+$/, t("מספר הטלפון חייב להתחיל בספרה 0 ולהכיל רק ספרות"))
        .required(t("required")),
      email: Yup.string()
        .email(t("כתובת אימייל לא תקינה"))
        .matches(
          /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/g,
          t("כתובת אימייל לא תקינה")
        )
        .required(t("required")),
      password: isForUpdate
        ? Yup.string()
            .min(6, t("אורך הסיסמה חייב להיות לפחות 6 תווים"))
            .matches(
              /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!\\?\\*\\.])[A-Za-z0-9!\\?\\*\\.]{6,16}$/,
              t("סיסמה לא תקינה")
            )
            .nullable()
        : Yup.string()
            .min(6, t("אורך הסיסמה חייב להיות לפחות 6 תווים"))
            .matches(
              /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!\\?\\*\\.])[A-Za-z0-9!\\?\\*\\.]{6,16}$/,
              t("סיסמה לא תקינה")
            )
            .required(t("required")),
      rolesCodeList: Yup.array()
        .of(Yup.number())
        .test("at-least-one-element", t("required"), (value) => {
          return value && value.length >= 1;
        }),
      tz: Yup.string()
        .max(9, t("חריגה מכמות התווים"))
        .test("isValidIsraeliID", t("תעודת זהות לא תקינה"), (value) =>
          isValidIsraeliID(value)
        )
        .nullable(),
    });

  const getTooltipForPassword = () => {
    return (
      <Tooltip id="tooltip" style={{ fontSize: "14px" }}>
        <ul className="password-tooltip">
          <li style={{ listStyle: "inside" }}>{t("mustCapitalLetter")}</li>
          <li style={{ listStyle: "inside" }}>{t("mustLowerCaseLetter")}</li>
          <li style={{ listStyle: "inside" }}>{t("mustOneNumber")}</li>
          <li style={{ listStyle: "inside" }}>{t("mustOneSign")}</li>
        </ul>
      </Tooltip>
    );
  };

  const formikProps = {
    initialValues,
    onSubmit: submitForm,
    validationSchema,
  };

  return (
    <Modal
      size="lg"
      show={isOpen}
      onHide={() => setIsOpen(null)}
      aria-labelledby="contained-modal-title-vcenter"
      contentClassName="form-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isForUpdate ? t("updateControlle") : t("addController")}
        </Modal.Title>
      </Modal.Header>
      <Formik {...formikProps}>
        {({
          handleSubmit,
          handleChange,
          touched,
          values,
          errors,
          setFieldValue,
        }) => {
          return (
            <>
              <Modal.Body>
                <Form>
                  <Row className="mb-4">
                    {isForUpdate && (
                      <Col className="px-3">
                        <Form.Group>
                          <FloatingLabel
                            controlId="floatingInput"
                            className="text-right"
                            label={t("controllerId")}
                          >
                            <Form.Control
                              type="text"
                              name="id"
                              value={values.id}
                              disabled
                            />
                          </FloatingLabel>
                        </Form.Group>
                      </Col>
                    )}
                    <Col className="px-3 col-6">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("controllerName")}
                        >
                          <Form.Control
                            type="text"
                            name="fullName"
                            value={values.fullName}
                            onChange={handleChange}
                            maxLength={20}
                            isInvalid={touched?.fullName && !!errors?.fullName}
                          />
                          {errors?.fullName && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.fullName}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("phone")}
                        >
                          <Form.Control
                            type="string"
                            name="phoneNumber"
                            value={values.phoneNumber}
                            onChange={handleChange}
                            isInvalid={
                              touched?.phoneNumber && !!errors?.phoneNumber
                            }
                            maxLength={10}
                          />
                          {errors?.phoneNumber && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.phoneNumber}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("email")}
                        >
                          <Form.Control
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            isInvalid={touched?.email && !!errors?.email}
                          />
                          {errors?.email && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.email}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("tz")}
                        >
                          <Form.Control
                            type="number"
                            name="tz"
                            value={values.tz}
                            onKeyDown={(e) =>
                              ["e", "E", "+", "-"].includes(e.key) &&
                              e.preventDefault()
                            }
                            onChange={(event) => {
                              const inputValue = event.target.value;
                              if (inputValue.length > 9) {
                                // Do not update the value if it exceeds the limit
                                return;
                              }
                              if (inputValue === "") {
                                // Handle erasing the value by setting it to an empty string
                                setFieldValue("tz", "");
                              } else {
                                setFieldValue("tz", inputValue);
                              }
                            }}
                            isInvalid={touched?.tz && !!errors?.tz}
                            maxLength={9}
                            min={0}
                          />
                          {errors?.tz && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.tz}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("password")}
                        >
                          <Form.Control
                            type="text"
                            name="password"
                            onChange={handleChange}
                            maxLength={20}
                            isInvalid={touched?.password && !!errors?.password}
                          />
                          {errors?.password && (
                            <Form.Control.Feedback type="invalid">
                              <OverlayTrigger
                                overlay={getTooltipForPassword()}
                                placement="bottom"
                              >
                                <InfoCircle />
                              </OverlayTrigger>
                              {errors?.password}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3  col-6">
                      <Form.Group>
                        <FloatingLabel
                          className="text-right"
                          label={t("userType")}
                        >
                          <Select
                            defaultValue={values?.rolesCodeList.map(
                              (code: number) =>
                                ControllerRoles.find(
                                  (opt: { value: number; label: string }) =>
                                    code === opt.value
                                )
                            )}
                            isMulti
                            name="rolesCodeList"
                            options={Object.keys(ControllerRoles).map(
                              (key) => ControllerRoles[key]
                            )}
                            isRtl
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={(
                              selected: { value: number; label: string }[]
                            ) => {
                              !selected.some(
                                (role) => role.value === UserRole.REGULAR
                              ) && setFieldValue("specialtiesCodeList", []);
                              setFieldValue(
                                "rolesCodeList",
                                selected.map((item) => item.value)
                              );
                            }}
                            placeholder={t("selectRolePlaceholder")}
                          />
                          {errors?.rolesCodeList && (
                            <span className="requiredField">
                              {errors?.rolesCodeList}
                            </span>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    {values?.rolesCodeList.includes(1) && (
                      <Col className="px-3 col-6">
                        <Form.Group>
                          <FloatingLabel
                            className="text-right"
                            label={t("specialty")}
                          >
                            <Select
                              defaultValue={values?.specialtiesCodeList.map(
                                (code) =>
                                  specialties.find(
                                    ({ value }) => value === code
                                  )
                              )}
                              isMulti
                              name="specialtiesCodeList"
                              options={specialties}
                              isRtl
                              className="basic-multi-select"
                              classNamePrefix="select"
                              onChange={(
                                selected: { value: number; label: string }[]
                              ) => {
                                setFieldValue(
                                  "specialtiesCodeList",
                                  selected.map((item) => item.value)
                                );
                              }}
                              placeholder={t("selectSpecialtyPlaceholder")}
                            />
                          </FloatingLabel>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          className="text-right"
                          label={t("lastUpdate")}
                        >
                          <DatePicker
                            className="form-control"
                            id="floatingInput"
                            name="lastReportUpdate"
                            selected={new Date(initialValues?.lastReportUpdate)}
                            dateFormat="dd/MM/yyyy"
                            disabled
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          className="text-right"
                          label={t("startDate")}
                        >
                          <DatePicker
                            className="form-control"
                            id="floatingInput"
                            name="registerDate"
                            selected={new Date(initialValues?.registerDate)}
                            dateFormat="dd/MM/yyyy"
                            disabled
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Modal.Body>
              <Modal.Footer className="flex-row">
                <Button variant="secondary" onClick={() => setIsOpen(null)}>
                  {t("cancel")}
                </Button>
                <Button variant="primary" onClick={() => handleSubmit()}>
                  {isForUpdate ? t("update") : t("add")}
                </Button>
              </Modal.Footer>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddOrUpdateModal;
